import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePMT3Selection } from '@/context/PMT3SelectionContext';
import { useAsinMonthlyResults } from '@/hooks/useAsinMonthlyResults';
import { getCurrentAndPreviousMonthsMoM } from '@/utils/monthOverMonthCalculations';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps
} from 'recharts';

interface ChartDataPoint {
  month: string;
  totalRevenue: number;
  totalGrossProfit: number;
  ppcSales: number;
  ppcSpend: number;
  acos: number;
}

export const MonthOverMonthChartsDashboard: React.FC = () => {
  const { selectedCompany, selectedProduct } = usePMT3Selection();
  const { data: monthlyData, isLoading, error } = useAsinMonthlyResults(
    selectedCompany,
    selectedProduct?.id
  );

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-gray-900">
            Month Over Month Charts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-500">Loading chart data...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-gray-900">
            Month Over Month Charts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-red-500">Error loading data: {error.message}</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!selectedProduct && !isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-gray-900">
            Month Over Month Charts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-500">Select a product to view month over month charts.</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!monthlyData && !isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-gray-900">
            Month Over Month Charts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-500">No monthly results data available for this ASIN.</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Get the relevant months (15 months)
  const relevantMonths = getCurrentAndPreviousMonthsMoM();
  
  // Prepare data for Recharts
  const chartData: ChartDataPoint[] = relevantMonths.map(month => {
    const monthData = monthlyData?.monthlyMetrics?.[month.fullName];
    return {
      month: month.abbreviation,
      totalRevenue: monthData?.totalRevenue || 0,
      totalGrossProfit: monthData?.totalGrossProfit || 0,
      ppcSales: monthData?.ppcSales || 0,
      ppcSpend: monthData?.ppcSpend || 0,
      acos: (monthData?.acos || 0) * 100, // Convert to percentage
    };
  }).reverse(); // Reverse to show chronological order

  // Custom tooltip formatters
  const formatCurrency = (value: number): string => `$${value.toLocaleString()}`;
  const formatPercentage = (value: number): string => `${value.toFixed(1)}%`;

  // Custom tooltip component for revenue/profit chart
  const RevenueTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg">
          <p className="text-gray-900 dark:text-gray-100 font-medium">{`Month: ${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {`${entry.name}: ${formatCurrency(entry.value || 0)}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Custom tooltip component for PPC chart
  const PPCTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg">
          <p className="text-gray-900 dark:text-gray-100 font-medium">{`Month: ${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.dataKey === 'acos' 
                ? `${entry.name}: ${formatPercentage(entry.value || 0)}`
                : `${entry.name}: ${formatCurrency(entry.value || 0)}`
              }
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader className="p-4">
        <CardTitle className="text-2xl font-bold">Month Over Month Charts</CardTitle>
        {selectedProduct && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Showing month-over-month trends for {selectedProduct.id}
          </p>
        )}
      </CardHeader>
      <CardContent className="p-4 space-y-8">
        {/* Chart 1: Total Revenue and Total Gross Profit */}
        <div className="bg-white dark:bg-gray-900 p-4 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
            Total Revenue vs Total Gross Profit
          </h3>
          <div style={{ height: '400px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="month" 
                  className="text-xs"
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  className="text-xs"
                  tick={{ fontSize: 12 }}
                  tickFormatter={formatCurrency}
                />
                <Tooltip content={<RevenueTooltip />} />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="totalRevenue" 
                  stroke="#3b82f6" 
                  strokeWidth={3}
                  name="Total Revenue"
                  dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="totalGrossProfit" 
                  stroke="#22c55e" 
                  strokeWidth={3}
                  name="Total Gross Profit"
                  dot={{ fill: '#22c55e', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#22c55e', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Chart 2: PPC Sales, PPC Spend, and ACoS */}
        <div className="bg-white dark:bg-gray-900 p-4 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
            PPC Sales, PPC Spend, and ACoS
          </h3>
          <div style={{ height: '400px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="month" 
                  className="text-xs"
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  yAxisId="left"
                  className="text-xs"
                  tick={{ fontSize: 12 }}
                  tickFormatter={formatCurrency}
                />
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  className="text-xs"
                  tick={{ fontSize: 12 }}
                  tickFormatter={formatPercentage}
                />
                <Tooltip content={<PPCTooltip />} />
                <Legend />
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="ppcSales" 
                  stroke="#3b82f6" 
                  strokeWidth={3}
                  name="PPC Sales"
                  dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2 }}
                />
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="ppcSpend" 
                  stroke="#ef4444" 
                  strokeWidth={3}
                  name="PPC Spend"
                  dot={{ fill: '#ef4444', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#ef4444', strokeWidth: 2 }}
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="acos" 
                  stroke="#f59e0b" 
                  strokeWidth={3}
                  name="ACoS"
                  dot={{ fill: '#f59e0b', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#f59e0b', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Footer information */}
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Showing data across {chartData.length} months • Last updated: {new Date().toLocaleString()}
        </div>
      </CardContent>
    </Card>
  );
};